import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, forwardRef, memo, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import typography from "../../../theme/typography";
import { colors } from "../../../theme/palette";
import MapWithPlacePicker from "./Components/MapWithPlacePicker";
import { useCreateEventContext } from "./cxt";

const formControlStyle = {
  display: "flex",
  flexDirection: "column",
  alignITems: "flex-start",
  rowGap: "6px",
  width: "100%",
};

const textFieldStyle = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px",
  color: colors.Gray400,
  width: "100%",

  "& .MuiInputBase-root": {
    border: `1px solid ${colors.Gray200}`,
    background: "#FFF",
  },
};

type Location = {
  formattedAddress: string;
  lat: number;
  lng: number;
};

type FormData = {
  eventName: string;
  descriptionRules: string;
  matchPrivacy: string;
  numberOfPlayers: [number, number];
  location: Location | null;
};

const FillData = forwardRef((_, ref) => {
  const { t } = useTranslation();
  const { state } = useCreateEventContext();

  const [formData, setFormData] = useState<FormData>({
    eventName: state.eventName,
    descriptionRules: state.descriptionRules,
    matchPrivacy: state.matchPrivacy,
    numberOfPlayers: state.numberOfPlayers,
    location: state.location,
  });

  const [errors, setErrors] = useState({
    eventName: "",
    descriptionRules: "",
    matchPrivacy: "",
    numberOfPlayers: "",
    location: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {
      eventName: "",
      descriptionRules: "",
      matchPrivacy: "",
      numberOfPlayers: "",
      location: "",
    };

    if (!formData.eventName) {
      newErrors.eventName = t("pages.events.createEvent.eventNameRequired");
      valid = false;
    }
    if (!formData.descriptionRules) {
      newErrors.descriptionRules = t("pages.events.createEvent.descriptionRequired");
      valid = false;
    }
    if (!formData.location || !formData.location.formattedAddress) {
      newErrors.location = t("pages.events.createEvent.eventLocationRequired");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  useImperativeHandle(ref, () => ({
    validateForm,
    formData,
  }));

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setFormData({
      ...formData,
      numberOfPlayers: [formData.numberOfPlayers[0], newValue as number],
    });
  };

  const handleLocationChange = (location: Location | null, error?: string) => {
    if (error || !location || !location.formattedAddress) {
      setErrors((prev) => ({
        ...prev,
        location: t("pages.events.createEvent.eventLocationRequired"),
      }));
    } else {
      setFormData((prev) => ({ ...prev, location }));
      setErrors((prev) => ({ ...prev, location: "" }));
    }
  };

  return (
    <Box component="form">
      <Typography
        variant="body16Bold"
        sx={{
          textAlign: "left",
          display: "block",
          marginBottom: "32px",
        }}
      >
        {t("pages.events.createEvent.setEventDataLocation")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          columnGap: "48px",
          marginBottom: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "32px",
            width: { xs: "100%", sm: "50%" },
          }}
        >
          <FormControl sx={formControlStyle}>
            <FormLabel
              sx={{
                textAlign: "start",
                ...typography.body14SemiBold,
                color: colors.Dark600,
              }}
            >
              {t("pages.events.createEvent.eventName")}
            </FormLabel>
            <TextField
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder={t("pages.events.createEvent.shortTitleMatch")}
              sx={textFieldStyle}
              error={!!errors.eventName}
              helperText={errors.eventName}
              inputProps={{
                maxLength: 100,
                autoComplete: "new-password",
                form: {
                  autoComplete: "off",
                },
              }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <FormLabel
              sx={{
                textAlign: "start",
                ...typography.body14SemiBold,
                color: colors.Dark600,
              }}
            >
              {t("pages.events.createEvent.descriptionRules")}
            </FormLabel>
            <TextField
              multiline
              rows={4}
              name="descriptionRules"
              value={formData.descriptionRules}
              onChange={handleChange}
              placeholder={t("pages.events.createEvent.letPeopleKnowMatch")}
              sx={{
                ...textFieldStyle,
                "& .MuiInputBase-root": {
                  borderRadius: "8px !important",
                },
              }}
              error={!!errors.descriptionRules}
              helperText={errors.descriptionRules}
              inputProps={{
                maxLength: 100,
                autoComplete: "new-password",
                form: {
                  autoComplete: "off",
                },
              }}
            />
          </FormControl>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="body16Bold"
              sx={{
                textAlign: "start",
                display: "block",
                marginBottom: "28px",
              }}
            >
              {t("pages.events.createEvent.numberOfPlayers")}
            </Typography>
            <Typography
              variant="body10Bold"
              sx={{
                textAlign: "end",
              }}
            >
              {formData.numberOfPlayers[1]}/44
            </Typography>
            <Slider
              value={formData.numberOfPlayers[1]}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value: number) => `${value} ${t("pages.events.createEvent.player")}`}
              min={8}
              max={44}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "32px",
            width: { xs: "100%", sm: "50%" },
          }}
        >
          <MapWithPlacePicker
            onLocationChange={handleLocationChange}
            locationError={errors.location}
            initialLocation={formData.location}
          />
        </Box>
      </Box>
      <FormControl sx={formControlStyle}>
        <FormLabel
          sx={{
            textAlign: "start",
            ...typography.body16ExtraBold,
            color: colors.Dark1000,
          }}
        >
          {t("pages.events.createEvent.matchPrivacy")}
        </FormLabel>
        <RadioGroup name="matchPrivacy" value={formData.matchPrivacy} onChange={handleChange}>
          <FormControlLabel
            sx={{
              width: "fit-content",
              textAlign: "start",
              "& .MuiFormControlLabel-label": {
                ...typography.body12Bold,
                color: colors.Dark600,
              },
            }}
            value="PUBLIC"
            control={<Radio />}
            label={t("pages.events.createEvent.publicMatch")}
          />
          <FormControlLabel
            sx={{
              width: "fit-content",
              textAlign: "start",
              "& .MuiFormControlLabel-label": {
                ...typography.body12Bold,
                color: colors.Dark600,
              },
            }}
            value="CLOSED"
            control={<Radio />}
            label={t("pages.events.createEvent.closedMatch")}
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );
});

export default memo(FillData);
